import { Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import {
  setManagerFormFillingScoreSettings,
  useFormFillingsByFeedbackFormId,
  useManagerFormFillingScoreSettings,
  useReviewCreationData,
} from 'reducers/reviewCreationSlice';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { Accordion } from 'uiToolkitV1/Accordion';
import { AccordionButton, AccordionItem2, AccordionPanel2 } from 'uiToolkitV1/Accordion/Accordion';
import { SelectionGridTable } from 'uiToolkitV1/SelectionGridTable';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextHeader4 } from 'uiToolkitV1/Typography';

import { useTemplateMetadata } from '../../Hooks/dataHooks/formFillingsDataHooks';
import { PreviewFormButton } from './PreviewFormButton';

export const ManagerReviewerSettings = ({ feedbackFormId }: { feedbackFormId: number }) => {
  const { reviewCycleId } = useReviewCreationData();
  const dispatch = useDispatch();

  const managerFormFillingScoreSettings = useManagerFormFillingScoreSettings({ feedbackFormId });
  const managerTemplateMetadata = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.MANAGER,
  });

  const dlmManagerTemplateMetadata = useTemplateMetadata({
    reviewCycleId,
    feedbackFormId,
    providerType: REVIEW_PROVIDER_TYPES.DLM,
  });

  const managerTemplateHasQuestions = managerTemplateMetadata?.template?.questionCount > 0;
  const dlmManagerTemplateHasQuestions = dlmManagerTemplateMetadata?.template?.questionCount > 0;
  const { enabledProviderTypes } = useFormFillingsByFeedbackFormId({ feedbackFormId });

  const isDLMReviewEnabled = enabledProviderTypes.includes(REVIEW_PROVIDER_TYPES.DLM);

  const hasGoalQuestions =
    managerTemplateMetadata.template?.templateSetting?.overallRatingDistribution?.PERFORMANCE > 0;
  const hasCompetencyQuestions =
    managerTemplateMetadata.template?.templateSetting?.overallRatingDistribution?.POTENTIAL > 0;

  const tableHeaders = [];
  if (hasGoalQuestions) tableHeaders.push('Overall Goals Score');
  if (hasCompetencyQuestions) tableHeaders.push('Overall Competency Score');
  tableHeaders.push('Overall Score');

  const tableOptions = [];
  if (hasGoalQuestions) {
    tableOptions.push({
      label: 'Show',
      checked: managerFormFillingScoreSettings?.isShowGoalRating,
    });
  }
  if (hasCompetencyQuestions) {
    tableOptions.push({
      label: 'Show',
      checked: true,
      disabled: true,
    });
  }
  tableOptions.push({
    label: 'Show',
    checked: true,
    disabled: true,
  });

  const onChangeHandlers = [];
  if (hasGoalQuestions) {
    onChangeHandlers.push((checked: boolean) =>
      dispatch(
        setManagerFormFillingScoreSettings({
          feedbackFormId,
          settings: { isShowGoalRating: checked },
        })
      )
    );
  }

  const handleChange = (checked: boolean, i: number, j: number) => {
    onChangeHandlers[j]?.(checked);
  };

  return (
    <Accordion defaultIndex={0} allowMultiple allowToggle boxShadow="none !important">
      <AccordionItem2 sxProps={{ borderBottom: 'none !important' }}>
        <AccordionButton>
          <Flex justifyContent="space-between" alignItems="center" flexGrow={1}>
            <TextHeader4 bold>
              {isDLMReviewEnabled ? 'Manager and Dotted Line Manager reviewers' : 'Manager reviewer'}
            </TextHeader4>
            <Flex justifyContent="flex-end" alignItems="center" flexGrow={1}>
              <PreviewFormButton
                feedbackFormId={feedbackFormId}
                providerType={REVIEW_PROVIDER_TYPES.MANAGER}
                isDisabled={!managerTemplateHasQuestions}
              />
              {isDLMReviewEnabled && (
                <PreviewFormButton
                  feedbackFormId={feedbackFormId}
                  providerType={REVIEW_PROVIDER_TYPES.DLM}
                  isDisabled={!dlmManagerTemplateHasQuestions}
                />
              )}
            </Flex>
          </Flex>
        </AccordionButton>
        <AccordionPanel2 pl="32px !important" mb="10px">
          <Flex flexDir="column" gap={3}>
            <Flex alignItems="center" gap={2}>
              <TextBodyMedium>
                Which scores can the Managers{isDLMReviewEnabled ? '/Dotted line managers' : ''} see before submitting
                the review forms?
              </TextBodyMedium>
              <TooltipInfoV1
                title={`Managers${
                  isDLMReviewEnabled ? ' and Dotted Line Managers reviewers' : ''
                } can view the average score for each section on the left side and at the top of the page while completing the review form`}
              />
            </Flex>
            <SelectionGridTable headers={tableHeaders} rows={[tableOptions]} onChange={handleChange} />
          </Flex>
        </AccordionPanel2>
      </AccordionItem2>
    </Accordion>
  );
};
